<template>
  <v-dialog :value="value" @input="$emit('input', $event)" persistent max-width="860" scrollable @click:outside="close">
    <v-card>
      <v-toolbar color="success darken-2" dark flat>
        <v-toolbar-title class="font-weight-bold text-subtitle-1">{{ $t('memberDataSection.greenCoffee.cuppingInformation') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-5" style="max-height: calc(90vh - 120px); overflow-y: auto;">
        <div v-if="displayId" class="text-body-2 mb-4 secondary--text font-weight-medium">
          {{ displayId }}
        </div>

        <div v-if="loading" class="d-flex justify-center align-center py-10">
          <v-progress-circular indeterminate color="success"></v-progress-circular>
        </div>

        <template v-else>
          <v-data-table
            :headers="headers"
            :items="list"
            :items-per-page="-1"
            class="cupping-table elevation-0"
            hide-default-footer
            disable-pagination
          >
            <template v-slot:item.cupping_date="{ item }">
              <div class="text-body-2">{{ formatDate(item.cupping_date) }}</div>
              <div class="text-caption grey--text">{{ item.cupping_name || '—' }}</div>
            </template>
            <template v-slot:item.roast="{ item }">
              <div class="text-body-2">{{ roastDisplay(item) }}</div>
              <div class="text-caption grey--text">{{ roastLevelLabel(item) }}</div>
            </template>
            <template v-slot:item.final_score="{ item }">
              <div class="d-flex align-center">
                <span class="font-weight-bold">{{ item.final_score != null ? item.final_score : '—' }}</span>
                <v-btn icon x-small class="ml-1" @click="toggleDetail(item)">
                  <v-icon small>{{ expandedId === item.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>

          <v-expand-transition>
            <div v-if="expandedRow" class="cupping-detail mt-4 pa-4 rounded border">
              <div class="text-subtitle-2 font-weight-bold mb-3">{{ $t('memberDataSection.greenCoffee.sessionDetails') }}</div>
              <v-simple-table dense class="detail-table">
                <tbody>
                  <tr><td class="label-cell">Flavour</td><td>{{ expandedRow.flavour != null ? expandedRow.flavour : '—' }}</td><td class="qualities-cell">Qualities: {{ formatQualities(expandedRow.flavour_qualities) }}</td></tr>
                  <tr><td class="label-cell">After Taste</td><td>{{ expandedRow.after_taste != null ? expandedRow.after_taste : '—' }}</td><td class="qualities-cell">Qualities: {{ formatQualities(expandedRow.after_taste_qualities) }}</td></tr>
                  <tr><td class="label-cell">Acidity</td><td>{{ expandedRow.acidity != null ? expandedRow.acidity : '—' }}</td><td class="qualities-cell">Qualities: {{ formatQualities(expandedRow.acidity_qualities) }}</td></tr>
                  <tr><td class="label-cell">Body</td><td>{{ expandedRow.body != null ? expandedRow.body : '—' }}</td><td class="qualities-cell">Qualities: {{ formatQualities(expandedRow.body_qualities) }}</td></tr>
                  <tr><td class="label-cell">Balance</td><td>{{ expandedRow.balance != null ? expandedRow.balance : '—' }}</td><td class="qualities-cell">Qualities: {{ formatQualities(expandedRow.balance_qualities) }}</td></tr>
                  <tr><td class="label-cell">Uniformity</td><td colspan="2">{{ expandedRow.uniformity != null ? expandedRow.uniformity : '—' }}</td></tr>
                  <tr><td class="label-cell">Clean Cup</td><td colspan="2">{{ expandedRow.clean_cup != null ? expandedRow.clean_cup : '—' }}</td></tr>
                  <tr><td class="label-cell">Sweetness</td><td colspan="2">{{ expandedRow.sweetness != null ? expandedRow.sweetness : '—' }}</td></tr>
                  <tr><td class="label-cell">Overall</td><td colspan="2">{{ expandedRow.overall != null ? expandedRow.overall : '—' }}</td></tr>
                  <tr><td class="label-cell">Defects</td><td colspan="2">{{ expandedRow.defect_value != null ? expandedRow.defect_value : '—' }}</td></tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-expand-transition>

          <div v-if="!loading && list.length === 0" class="text-center py-8 text-body-2 grey--text">
            {{ $t('memberDataSection.greenCoffee.noCuppingHistory') }}
          </div>
        </template>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="success" class="text-none" @click="close">{{ $t('memberDataSection.greenCoffee.close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import GreenCoffeeService from "@/_services/GreenCoffeeService";

export default {
  name: "CuppingHistoryModal",
  props: {
    value: { type: Boolean, default: false },
    greenCoffeeId: { type: [Number, String], default: null },
    displayId: { type: String, default: "" },
  },
  data() {
    return {
      loading: false,
      list: [],
      expandedId: null,
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t('memberDataSection.greenCoffee.date'), value: "cupping_date", sortable: true, width: "28%" },
        { text: this.$t('memberDataSection.greenCoffee.roast'), value: "roast", sortable: false, width: "36%" },
        { text: this.$t('memberDataSection.greenCoffee.score'), value: "final_score", sortable: false, width: "20%", align: "start" },
      ];
    },
    expandedRow() {
      if (!this.expandedId || !this.list.length) return null;
      return this.list.find((r) => r.id === this.expandedId) || null;
    },
  },
  watch: {
    value(v) {
      if (v && this.greenCoffeeId) this.fetchList();
      if (!v) this.expandedId = null;
    },
    greenCoffeeId(v) {
      if (v && this.value) this.fetchList();
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
      this.$emit("close");
    },
    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "—";
    },
    roastDisplay(item) {
      const t = item.roasting_temperature;
      const m = item.roasting_time;
      if (t != null && m != null) return `${Number(t)}°C / ${Number(m)} min`;
      if (t != null) return `${Number(t)}°C`;
      if (m != null) return `${Number(m)} min`;
      return "—";
    },
    roastLevelLabel(item) {
      const temp = item.roasting_temperature != null ? Number(item.roasting_temperature) : null;
      if (temp == null) return "";
      if (temp < 200) return "Light Roast";
      if (temp < 215) return "Medium Roast";
      if (temp < 230) return "Medium Dark Roast";
      return "Dark Roast";
    },
    formatQualities(arr) {
      if (!Array.isArray(arr) || !arr.length) return "—";
      return arr.join(", ");
    },
    toggleDetail(item) {
      this.expandedId = this.expandedId === item.id ? null : item.id;
    },
    async fetchList() {
      this.loading = true;
      this.list = [];
      this.expandedId = null;
      try {
        const res = await GreenCoffeeService.getCuppingHistory(this.greenCoffeeId);
        this.list = res?.data || [];
      } catch (e) {
        console.error("Failed to load cupping history:", e);
        this.$notify?.({ text: e?.msg || e?.message || "Failed to load cupping history.", type: "error" });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.cupping-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}
.label-cell {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
  width: 120px;
  padding: 6px 12px 6px 0;
}
.qualities-cell {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.85rem;
}
.detail-table >>> td {
  vertical-align: top;
}
.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
